import { z } from 'zod';
import {
  ActionStatusSchema,
  ExternalSystemSchema,
  ImplementationOwnerTypeSchema,
  PrioritySchema,
} from './enums';
import {
  ActionIdSchema,
  AssessmentIdSchema,
  FindingIdSchema,
  MappingIdSchema,
  QrsControlIdSchema,
  RecommendationIdSchema,
  TenantIdSchema,
  UserIdSchema,
} from './ids';
import { NonEmptyStringSchema, SemVerSchema, TimestampSchema } from './primitives';

/**
 * Control mapping (DESIGN_SPEC §7.7, §8.5, docs/DECISIONS.md ZD-059).
 *
 * The Control Mapping worker must cite an internal pack mapping; it cannot
 * invent controls, map indiscriminately to every framework, or change severity.
 * It is **deterministic**, not hybrid: the linkage is `ThreatPattern
 * .control_requirements`, pack content with no per-assessment judgement in it,
 * so there is nothing here for a model to decide (ZD-059 corrects DESIGN_SPEC
 * §6.2, which still says `hybrid`).
 *
 * The union is deliberate. §5's guard on `control_mapping_complete` requires
 * every finding to have at least one mapping **or an explicit "no applicable
 * control"**. Modelling that second case as a first-class record rather than an
 * absence means the guard can tell "nobody looked" apart from "somebody looked
 * and there is nothing" — and the reviewer can see which it was. It ships
 * unexercised: `ThreatPattern.control_requirements` is schema-enforced
 * non-empty, so 0 of 65 patterns can produce it today. Reopens the day a
 * pattern is authored for a threat QRS has no catalogue control for yet.
 *
 * **No `confidence` field, and that is a correction rather than an omission**
 * (ZD-059). It was typed `ConfidenceSchema` with nothing restricting who could
 * set it — the live version of the mistake ZD-058 §5 closed for
 * `default_scoring_inputs`. The mapping asserts nothing beyond what the
 * pattern already asserts, so it has no structural signal of its own to derive
 * a confidence from; the only honest value would be a copy of the finding's
 * own `confidence`, which goes stale the moment the finding is re-scored
 * (ZD-017's third fact). A reader wanting to know how confident this mapping
 * is reads it off `finding_id`.
 */
const MappingBase = {
  mapping_id: MappingIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  finding_id: FindingIdSchema,
  /**
   * A rendered sentence, not authored free text — a transcript of the
   * pattern's own `control_requirements`, computed the same way for every
   * instance of one (pattern, control) pair (docs/DECISIONS.md ZD-031 §2's
   * test applied here: a reader could not disagree with it while agreeing
   * with the pack).
   */
  mapping_rationale: NonEmptyStringSchema,
  /** The QRS control catalogue version in force (ZD-032), pinned per §12.2. */
  control_pack_version: SemVerSchema,
  created_at: TimestampSchema,
};

/**
 * A finding mapped to a **QRS control**, never to a framework identifier
 * (docs/DECISIONS.md ZD-032).
 *
 * The framework view — ISO A.8.24, NIST PR.DS-02 — is rendered from the
 * catalogue's mapping rows at read time, with each row's `fidelity` and
 * `framework_version` intact. Carrying framework text here instead would put
 * the licensing question in the mapping worker as well as in the packs, and
 * would lose the fidelity distinction on the way into a client's evidence pack.
 */
export const MappedControlSchema = z.object({
  ...MappingBase,
  kind: z.literal('mapped'),
  qrs_control_id: QrsControlIdSchema,
});

export const NoApplicableControlSchema = z.object({
  ...MappingBase,
  kind: z.literal('no_applicable_control'),
});

export const ControlMappingSchema = z.discriminatedUnion('kind', [
  MappedControlSchema,
  NoApplicableControlSchema,
]);
export type ControlMapping = z.infer<typeof ControlMappingSchema>;

/** Recommendation (DESIGN_SPEC §7.8). Template-bound and actionable. */
export const RecommendationSchema = z.object({
  rec_id: RecommendationIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  finding_id: FindingIdSchema,
  title: NonEmptyStringSchema,
  description: NonEmptyStringSchema,
  requirement: NonEmptyStringSchema,
  acceptance_criteria: z.array(NonEmptyStringSchema),
  priority: PrioritySchema,

  /** Never guessed — §6.2 permits an owner only where one is actually known. */
  implementation_owner_type: ImplementationOwnerTypeSchema.nullable(),

  evidence_required: z.array(z.string()),

  /** The pack template this was generated from, or null if written by hand. */
  template_id: z.string().nullable(),

  created_at: TimestampSchema,
});
export type Recommendation = z.infer<typeof RecommendationSchema>;

/**
 * The action register (DESIGN_SPEC §7.8, §14.3). `external_ref` is the slot the
 * future MCP ticketing integration writes into (§16) — the integration is
 * additive precisely because this field exists now.
 */
export const ExternalRefSchema = z.object({
  system: ExternalSystemSchema.nullable(),
  ticket_id: z.string().nullable(),
});
export type ExternalRef = z.infer<typeof ExternalRefSchema>;

export const ActionSchema = z.object({
  action_id: ActionIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  rec_id: RecommendationIdSchema,
  status: ActionStatusSchema,
  owner_user_id: UserIdSchema.nullable(),
  due_at: TimestampSchema.nullable(),
  external_ref: ExternalRefSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Action = z.infer<typeof ActionSchema>;
